import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ExternalLink, Music, Calendar, Instagram, Facebook, Youtube } from 'lucide-react';
import { PageLayout } from '../components/layouts/PageLayout';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ArtistDetailPage = () => {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleElements, setVisibleElements] = useState([]);

  const fetchArtistData = useCallback(async () => {
    try {
      setLoading(true);
      const [artistRes, releasesRes] = await Promise.all([
        axios.get(`${API_URL}/api/artists/${artistId}`),
        axios.get(`${API_URL}/api/artists/${artistId}/releases`)
      ]);
      setArtist(artistRes.data);
      setReleases(releasesRes.data);
    } catch (error) {
      console.error('Failed to fetch artist data:', error);
    } finally {
      setLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    fetchArtistData();
  }, [fetchArtistData]);

  useEffect(() => {
    // Intersection Observer for scroll-based animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [artist, releases]);

  const socialLinks = artist ? [
    { icon: ExternalLink, url: artist?.externalLink, label: 'Website', color: 'purple' },
    { icon: Music, url: artist?.soundcloudUrl, label: 'SoundCloud', color: 'orange' },
    { icon: Instagram, url: artist?.instagram, label: 'Instagram', color: 'pink' },
    { icon: Facebook, url: artist?.facebook, label: 'Facebook', color: 'blue' },
    { icon: Youtube, url: artist?.youtube, label: 'YouTube', color: 'red' },
    { icon: Music, url: artist?.spotify, label: 'Spotify', color: 'green' },
    { icon: Music, url: artist?.bandcamp, label: 'Bandcamp', color: 'cyan' },
  ].filter(link => link.url) : [];

  if (loading) {
    return (
      <PageLayout primaryColor="orange" secondaryColor="green">
        <div className="page-transition min-h-screen">
          <Navigation />
          <div className="pt-32 container mx-auto px-4 text-center">
            <p className="text-white text-xl">Loading...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!artist) {
    return (
      <PageLayout primaryColor="orange" secondaryColor="green">
        <div className="page-transition min-h-screen">
          <Navigation />
          <div className="pt-32 container mx-auto px-4 text-center">
            <p className="text-white text-xl">Artist not found</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout primaryColor="orange" secondaryColor="green">
      <div className="page-transition min-h-screen">
        <Navigation />
        
        {/* Artist Header */}
        <section className="pt-32 pb-16">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Artist Image */}
              <div 
                className="w-full md:w-1/3"
                data-animate
                data-index="image"
                style={{
                  opacity: visibleElements.includes('image') ? 1 : 0,
                  transform: visibleElements.includes('image') ? 'translateX(0)' : 'translateX(-30px)',
                  transition: 'all 0.7s ease-out 0.2s'
                }}
              >
                <div className="relative aspect-square overflow-hidden rounded-2xl shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/60 hover:scale-105 transition-all duration-500 group cursor-pointer">
                  <img 
                    src={artist.image} 
                    alt={artist.name}
                    referrerPolicy="no-referrer"
                    
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>

              {/* Artist Info */}
              <div 
                className="w-full md:w-2/3"
                data-animate
                data-index="info"
                style={{
                  opacity: visibleElements.includes('info') ? 1 : 0,
                  transform: visibleElements.includes('info') ? 'translateX(0)' : 'translateX(30px)',
                  transition: 'all 0.7s ease-out 0.3s'
                }}
              >
                <h1 className="text-5xl md:text-7xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 hover:scale-105 transition-transform duration-300 inline-block cursor-default leading-tight pb-2">
                  {artist.name}
                </h1>
                
                {artist.type && (
                  <div className="mb-6 flex flex-wrap gap-2">
                    {(Array.isArray(artist.type) ? artist.type : [artist.type]).map((type) => (
                      <Badge 
                        key={type}
                        className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0 text-base px-5 py-2 shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-300"
                      >
                        {type}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <p className="text-gray-300 text-lg mb-8 leading-relaxed whitespace-pre-wrap hover:text-gray-200 transition-colors duration-300">
                  {artist.bio}
                </p>

                {/* Social Media Links */}
                {socialLinks.length > 0 && (
                  <div className="flex flex-wrap gap-4">
                    {socialLinks.map((social) => {
                      const Icon = social.icon;
                      const getGradientClass = (color) => {
                        const gradients = {
                          purple: 'from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800',
                          orange: 'from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800',
                          pink: 'from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800',
                          blue: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
                          red: 'from-red-600 to-red-700 hover:from-red-700 hover:to-red-800',
                          green: 'from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                        };
                        return gradients[color] || 'from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800';
                      };
                      return (
                        <Button
                          key={social.label}
                          onClick={() => window.open(social.url, '_blank')}
                          className={`bg-gradient-to-r ${getGradientClass(social.color)} text-white shadow-lg hover:scale-105 transition-all duration-300`}
                        >
                          <Icon className="w-5 h-5 mr-2" />
                          {social.label}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Artist Releases */}
        <section className="py-16 bg-gradient-to-b from-transparent via-green-950/10 to-transparent">
          <div className="container mx-auto px-4">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-12 text-center text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-orange-400 hover:scale-105 transition-transform duration-300 inline-block cursor-default leading-tight pb-2"
              data-animate
              data-index="releases-header"
              style={{
                opacity: visibleElements.includes('releases-header') ? 1 : 0,
                transform: visibleElements.includes('releases-header') ? 'translateY(0)' : 'translateY(30px)',
                transition: 'all 0.7s ease-out'
              }}
            >
              Releases
            </h2>

            {releases.length === 0 ? (
              <p className="text-gray-400 text-center text-lg">No releases yet</p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {releases.map((release, index) => (
                  <Card 
                    key={release.id}
                    onClick={() => {
                      if (!release.isExternal) {
                        window.location.href = `/releases/${release.id}`;
                      }
                    }}
                    className={`bg-black/50 border-orange-500/30 overflow-hidden hover:border-orange-500 hover:shadow-2xl hover:shadow-orange-500/50 hover:-translate-y-2 transition-all duration-300 group p-0 ${!release.isExternal ? 'cursor-pointer' : ''}`}
                    data-animate
                    data-index={`release-${index}`}
                    style={{
                      opacity: visibleElements.includes(`release-${index}`) ? 1 : 0,
                      transform: visibleElements.includes(`release-${index}`) ? 'scale(1)' : 'scale(0.9)',
                      transition: `all 0.5s ease-out ${index * 0.1}s`
                    }}
                  >
                    <div className="relative overflow-hidden aspect-square">
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10"></div>
                      <img 
                        src={release.artwork}
                        alt={release.title}
                        referrerPolicy="no-referrer"
                        
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4 z-20 flex gap-2">
                        <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          {release.type}
                        </span>
                        {release.isExternal && (
                          <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                            External
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-300 leading-tight pb-1">
                        {release.title}
                      </h3>
                      
                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <Calendar className="w-4 h-4 mr-2" />
                        {release.releaseDate}
                      </div>

                      <Button 
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = release.isExternal ? release.externalUrl : release.soundcloudUrl;
                          window.open(url, '_blank');
                        }}
                        className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/50 text-white transition-all duration-300"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {release.isExternal ? 'View External' : 'Listen'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </PageLayout>
  );
};
